import React, { createContext, useContext, useEffect, useState } from "react";
import { io } from "socket.io-client";
import useAuth from "../hooks/useAuth";
import useInstantOrderSound from "../hooks/useInstantOrderSound";
import instantOrdersApi from "../api/instantOrders";

const Context = createContext<ReturnType<typeof useInstantOrderSound> | null>(null);

export function useInstantOrderAlerts() {
  const value = useContext(Context);
  if (!value) throw new Error("InstantOrderAlertsProvider is required");
  return value;
}

function Alerts({ children }: { children: React.ReactNode }) {
  const sound = useInstantOrderSound();
  const [pending, setPending] = useState<string[]>([]);
  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (!token) return;
    let disposed = false;
    let revision = 0;
    let fetching = false;
    const refresh = async () => {
      if (fetching) return;
      fetching = true;
      const startedAt = revision;
      try {
        const data = await instantOrdersApi.getQueue();
        // Socket updates that arrive during a request take precedence over its snapshot.
        if (!disposed && startedAt === revision) {
          setPending((data.orders || []).filter((order: any) => order.status === "PLACED").map((order: any) => order._id));
        }
      } catch (error) {
        console.warn("Could not refresh pending order alerts", error);
      } finally { fetching = false; }
    };
    const socket = io(process.env.REACT_APP_API_URL || window.location.origin, {
      auth: { token }, transports: ["websocket", "polling"],
      reconnectionAttempts: 15, reconnectionDelay: 2000,
    });
    socket.on("connect", () => { socket.emit("admin:join"); void refresh(); });
    const update = (order: any) => {
      if (!order?._id || !order.status) return;
      revision += 1;
      setPending(previous => order.status === "PLACED"
        ? previous.includes(order._id) ? previous : [...previous, order._id]
        : previous.filter(id => id !== order._id));
    };
    socket.on("instant_order:new", update);
    socket.on("instant_order:status", update);
    socket.on("instant_order:assigned", update);
    void refresh();
    const poll = window.setInterval(() => { void refresh(); }, 15000);
    const onVisible = () => { if (document.visibilityState === "visible") void refresh(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      disposed = true;
      socket.disconnect();
      window.clearInterval(poll);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  const hasPending = pending.length > 0;
  const { enabled, volume, blocked, play } = sound;
  useEffect(() => {
    if (!hasPending || !enabled || volume === 0) return;
    play();
    const timer = window.setInterval(() => play(), 5000);
    return () => window.clearInterval(timer);
  }, [hasPending, enabled, volume, blocked, play]);

  return <Context.Provider value={sound}>{children}</Context.Provider>;
}

export default function InstantOrderAlertsProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loading, admin } = useAuth();
  return isAuthenticated && !loading && admin?.permissions?.instantOrders
    ? <Alerts key={admin.id}>{children}</Alerts> : <>{children}</>;
}
